/**
 * Vendor logos, stored on the vendor document as a small image data URL.
 *
 * The clinic's invoice prints this logo. A data URL on the document (rather
 * than a Storage object) is read by the desktop app in the same request as the
 * vendor's name and address, and keeps working from its cached copy offline.
 * Firestore caps a document at 1 MiB, so the upload is scaled down first: a
 * receipt-printer logo is under 60 px tall, and 320 px is plenty.
 */

export const LOGO_MAX_WIDTH = 320;
export const LOGO_MAX_HEIGHT = 160;
/** Leaves most of the 1 MiB document for everything else on it. */
export const LOGO_MAX_CHARS = 300_000;

/** Scale (w, h) down to fit the box, never up. */
export function fitWithin(width, height, maxW = LOGO_MAX_WIDTH, maxH = LOGO_MAX_HEIGHT) {
  if (!width || !height) return { width: 0, height: 0 };
  const k = Math.min(1, maxW / width, maxH / height);
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the logo file'));
    reader.readAsDataURL(file);
  });
}

function decode(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('That file is not an image this browser can read'));
    img.src = src;
  });
}

/**
 * An uploaded file → a PNG data URL no larger than the logo box.
 * PNG keeps a transparent logo transparent on the white receipt; a photo-like
 * logo that is still too large as PNG is re-encoded as JPEG on white.
 */
export async function logoFileToDataUrl(file) {
  if (!file || !/^image\//.test(file.type)) throw new Error('Please choose an image file (PNG or JPG)');
  const img = await decode(await readAsDataUrl(file));
  const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height);
  if (!width || !height) throw new Error('That image is empty');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  let url = canvas.toDataURL('image/png');
  if (url.length > LOGO_MAX_CHARS) {
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    url = canvas.toDataURL('image/jpeg', 0.85);
  }
  if (url.length > LOGO_MAX_CHARS) throw new Error('That logo is too detailed to store; please use a simpler image');
  return url;
}
